import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { Box, IconButton, Link, Paper, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Drawer from "./Drawer";
import Header from "./Header";
import { openDrawer } from "store/reducers/menu";
import { useDynamicBreakpoints } from "../../utils/breakpointUtils";
import SystemBanner from "../../components/Banner/SystemBanner";
import { BRAND_TYPE } from "../../config";

const HELP_DOC_URL = "https://boxphishsupport.helpdocs.io/article/cqswcwx69j-logs";
const LOGS_BANNER_DISMISSED_KEY = "logsRelocationBannerDismissed";
const LOGS_BANNER_DISMISSED_TIMESTAMP_KEY = "logsRelocationBannerDismissedTimestamp";

const M365_GUIDE_URL = BRAND_TYPE === "censornet"
    ? "https://help.clouduss.com/safelisting/sat-m-365-masked-url-update-06-2026"
    : "https://boxphishsupport.helpdocs.io/article/5bcb8ich0f-m-365-allowlisting-masked-url-update-06-2026";
const M365_BANNER_DISMISSED_KEY = "m365MaskedUrlBannerDismissed";
const M365_BANNER_DISMISSED_TIMESTAMP_KEY = "m365MaskedUrlBannerDismissedTimestamp";

const MainLayout = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [bannerDismissed, setBannerDismissed] = useState(false);
    const [m365BannerDismissed, setM365BannerDismissed] = useState(false);

    const user = JSON.parse(localStorage.getItem("user"));
    const isUser = user && user?.role === "";
    const isAdmin = user?.role === "Administrators" || user?.role?.includes("TenancyAdministrators");
    const { drawerOpen } = useSelector((state) => state.menu);
    const { isLoggedIn } = useSelector((state) => state.auth);
    const { tenancyId } = useSelector((state) => state.tenancy);

    useEffect(() => {
        const dismissed = localStorage.getItem(LOGS_BANNER_DISMISSED_KEY);
        setBannerDismissed(dismissed === "true");
        const m365Dismissed = localStorage.getItem(M365_BANNER_DISMISSED_KEY);
        setM365BannerDismissed(m365Dismissed === "true");
    }, []);

    const handleBannerDismiss = () => {
        localStorage.setItem(LOGS_BANNER_DISMISSED_KEY, "true");
        localStorage.setItem(LOGS_BANNER_DISMISSED_TIMESTAMP_KEY, Date.now().toString());
        setBannerDismissed(true);
    };

    const handleM365BannerDismiss = () => {
        localStorage.setItem(M365_BANNER_DISMISSED_KEY, "true");
        localStorage.setItem(M365_BANNER_DISMISSED_TIMESTAMP_KEY, Date.now().toString());
        setM365BannerDismissed(true);
    };

    // TrustLayer (censornet) helpdocs do not yet cover the relocated logs, so the logs banner is hidden for that brand.
    const showLogsPopup = isAdmin && !bannerDismissed && BRAND_TYPE !== "censornet";
    const showM365Banner = isAdmin && !m365BannerDismissed;

    const { matchDownSM, matchDownMD, matchDownLG } = useDynamicBreakpoints(drawerOpen);

    const handleDrawerToggle = () => {
        dispatch(openDrawer({ drawerOpen: !drawerOpen }));
    };

    useEffect(() => {
        // Only redirect if not logged in and not already navigating to login
        if (!isLoggedIn && location.pathname !== '/login') {
            navigate("/login");
        }
    }, [isLoggedIn, navigate, location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 800 && drawerOpen) {
                dispatch(openDrawer({ drawerOpen: false }));
            }
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [drawerOpen, dispatch]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", minHeight: "100vh", paddingTop: showM365Banner ? 'var(--banner-height, 0px)' : 0 }}>

            <SystemBanner show={showM365Banner} onDismiss={handleM365BannerDismiss}>
                Microsoft Tenancies &ndash; please update your existing allowlisting to include our new masked URLs:{' '}
                <Link
                    href={M365_GUIDE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        color: 'inherit',
                        textDecoration: 'underline',
                        fontWeight: 700,
                        '&:hover': { textDecoration: 'none' }
                    }}
                >
                    M365 Masked URL Update 06/2026
                </Link>
            </SystemBanner>

            <Box>
                <Header open={drawerOpen} showBanner={showM365Banner} />
            </Box>

            <Box sx={{ display: "flex", width: "100%", bgcolor: 'background.default', flex: 1, position: 'relative' }}>
                <Drawer open={drawerOpen} handleDrawerToggle={handleDrawerToggle} showBanner={showM365Banner} />

                <Box component="main" sx={{ width: drawerOpen ? "calc(100% - 260px)" : "100%", flexGrow: 1, p: { xs: 2, sm: 3 }, mt: -2 }}>
                    <Outlet />
                </Box>
            </Box>

            {showLogsPopup && (
                <Paper
                    elevation={3}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1500,
                        maxWidth: 640,
                        width: 'calc(100% - 48px)',
                        bgcolor: '#fff',
                        color: '#000',
                        fontFamily: '"Inter", sans-serif',
                        border: '1px solid rgba(0, 0, 0, 0.25)',
                        borderRadius: 2,
                        padding: '10px 12px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1
                    }}
                >
                    <NotificationsIcon sx={{ fontSize: 16, mt: '2px', color: '#000' }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', color: '#000', fontFamily: '"Inter", sans-serif', fontWeight: 500, lineHeight: 1.4, fontSize: '0.75rem' }}
                        >
                            Logs have a new home with more capabilities! Click{' '}
                            <Link
                                component="button"
                                type="button"
                                onClick={() => navigate("/logs")}
                                sx={{
                                    color: 'secondary.main',
                                    textDecoration: 'underline',
                                    fontWeight: 700,
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    font: 'inherit',
                                    cursor: 'pointer',
                                    verticalAlign: 'baseline',
                                    '&:hover': { color: 'secondary.dark', textDecoration: 'none' }
                                }}
                            >
                                HERE
                            </Link>
                            {' '}to go directly to them. Check out the{' '}
                            <Link
                                href={HELP_DOC_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: '#000',
                                    textDecoration: 'underline',
                                    fontWeight: 600,
                                    '&:hover': { textDecoration: 'none' }
                                }}
                            >
                                Helpdoc
                            </Link>
                            {' '}for a rundown.
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleBannerDismiss}
                        size="small"
                        sx={{
                            color: '#000',
                            padding: '2px',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Paper>
            )}
        </Box>
    );
};

export default MainLayout;
